import {
  CHANNEL_MEMBER_JOINED,
  CHANNEL_MEMBER_LEFT,
  CHANNEL_UPDATED,
} from '@common/constants/webSockets'
import { isDmKind } from '@common/helpers/channelKind'
import { IChannelMembershipEvent } from '@common/interfaces/channels'
import { Middleware } from '@reduxjs/toolkit'
import { Action } from 'redux'
import { getChannel, listChannels, listConversationGroups } from './channels/actions'
import { IRootState } from './reducer'

/**
 * Keep the sidebar and the open conversation honest for people who did not make
 * the change, off the websocket alone.
 *
 * Three refetches, each covering something no reducer can derive from the event
 * it reacts to:
 *
 * - **A channel the sidebar has never heard of** (`CHANNEL_MEMBER_JOINED`).
 *   `channels/reducer.ts` patches `activeChannel.members` when the event names
 *   the channel already open, and never inserts a channel it doesn't hold — so a
 *   conversation opened for someone else (`resolveDmChannel`) is invisible to
 *   every participant but the one who followed the request's own response. A
 *   named channel lives in the flat list and a DM lives inside its participant
 *   group, so the event's `dmKey` decides which list is reread.
 * - **The open conversation's own header** (`CHANNEL_MEMBER_JOINED`/`LEFT`). A DM
 *   with no stored name is titled from its participants, and that title is
 *   resolved server-side in `channels/Get` — so a conversation with Bob that
 *   gains Jane keeps saying "Bob" until it is reopened.
 * - **The sidebar's conversation groups** (`CHANNEL_UPDATED` carrying a `dmKey`).
 *   A grouped conversation is filed under the fingerprint of who is in it, so
 *   adding someone moves it to a group that may not exist yet — as does bringing
 *   back one the recipient had removed. Only the server assembles those rows and
 *   their rolled-up counts.
 *
 * A middleware rather than reducer cases because all three need data the events
 * do not carry — unread counts, last-message previews, participant-derived
 * titles — and the list/get endpoints are the only places that assemble it.
 */
const channelMembershipMiddleware = (): Middleware<object, IRootState> => (store) => {
  // One in-flight refetch per unknown channel: opening a conversation broadcasts
  // one CHANNEL_MEMBER_JOINED per member, so a client with N co-members
  // otherwise fires N redundant list requests off the same event.
  const pendingChannelIds = new Set<string>()
  // Likewise for the open conversation: adding three people at once is three
  // join events, and one reread of the channel answers all of them.
  const pendingDetailChannelIds = new Set<string>()

  // `dispatch` is typed to return the action, but the promise middleware
  // (`apiMiddleware.ts`) actually resolves it once the request settles — same
  // runtime behavior `webSocketMiddleware.ts`'s resync relies on.
  const dispatchRequest = (request: unknown): Promise<unknown> =>
    store.dispatch(request as Action) as unknown as Promise<unknown>

  const refetchOnce = async (
    pending: Set<string>,
    channelId: string,
    request: unknown,
  ): Promise<void> => {
    pending.add(channelId)
    try {
      await dispatchRequest(request)
    } finally {
      pending.delete(channelId)
    }
  }

  const isKnownChannel = (channelId: string, dmKey: string | null): boolean => {
    const state = store.getState().channels
    if (!dmKey) return !!state?.channels?.some((channel) => channel.id === channelId)
    return !!state?.conversationsByGroup?.[dmKey]?.items.some((item) => item.id === channelId)
  }

  /** The sidebar row is missing entirely, so the list it belongs in is reread. */
  const refetchUnknownChannel = (channelId: string, dmKey: string | null): void => {
    if (pendingChannelIds.has(channelId)) return
    if (isKnownChannel(channelId, dmKey)) return
    void refetchOnce(
      pendingChannelIds,
      channelId,
      dmKey ? listConversationGroups() : listChannels(),
    )
  }

  /**
   * Only for a DM: a named channel's title is stored, so its header cannot go
   * stale behind a roster change, and joining one is common enough that
   * rereading it on every join would be pure traffic.
   */
  const refetchActiveConversation = (channelId: string): void => {
    if (pendingDetailChannelIds.has(channelId)) return
    const active = store.getState().channels?.activeChannel
    if (active?.id !== channelId || !isDmKind(active.kind)) return
    void refetchOnce(pendingDetailChannelIds, channelId, getChannel(channelId))
  }

  return (next) => (action: { type?: string; data?: unknown }) => {
    if (
      action.type !== CHANNEL_MEMBER_JOINED &&
      action.type !== CHANNEL_MEMBER_LEFT &&
      action.type !== CHANNEL_UPDATED
    ) {
      return next(action)
    }

    const data = action.data as Partial<IChannelMembershipEvent> | undefined
    const channelId = data?.channelId
    if (!channelId) return next(action)

    const dmKey = data?.dmKey ?? null

    if (action.type === CHANNEL_UPDATED) {
      // Sent only when the receiver may have to re-file the row, so its mere
      // presence is the signal — see `broadcastChannelUpdated`. The reducer takes
      // the row out of the group it left; the groups themselves (and their
      // summed badges) come from here.
      if (dmKey) void dispatchRequest(listConversationGroups())
      return next(action)
    }

    if (action.type === CHANNEL_MEMBER_JOINED) refetchUnknownChannel(channelId, dmKey)
    refetchActiveConversation(channelId)

    return next(action)
  }
}

export default channelMembershipMiddleware
